import { Field } from '@atlaskit/form';
import { Stack, Text } from '@atlaskit/primitives';
import { currentBridgeData } from '@databases/database-state/state/bridge-data';
import FieldPicker from '@databases/field-picker/FieldPicker';
import PropertyPicker from '@databases/field-picker/PropertyPicker';
import { messages as intlMessages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { type DatabaseField, DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  getDatabaseFieldOptional,
  getDatabaseFields
} from '@databases/utils/database/utils';
import { loadSupportedFields } from '@databases/utils/redux/datasources/actions';
import { selectSupportedProperties } from '@databases/utils/redux/datasources/selectors';
import React, { type FunctionComponent, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from './messages.ts';

type PageDetailsFieldSettingsProps = {
  contentId: string;
  field: DatabaseField<DatabaseFieldType.PAGE_DETAILS>;
};

const isPageLinkField = (
  field: DatabaseField | undefined
): field is DatabaseField<DatabaseFieldType.PAGE_LINK> => {
  return field?.type === DatabaseFieldType.PAGE_LINK;
};

const PageDetailsFieldSettings: FunctionComponent<PageDetailsFieldSettingsProps> = ({
  contentId,
  field
}) => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();
  const cloudId = currentBridgeData.data.cloudId;

  const [sourceField, sourceFields, propertyOptions] = defaultStoreUtils.useEqualSelector(
    (state) => {
      const databaseDraftWithoutDeleted = selectDatabaseDraftByContentId(state, {
        contentId,
        includeDeleted: false
      });
      const selectedSourceField = getDatabaseFieldOptional(
        databaseDraftWithoutDeleted,
        field.settings.fieldId
      );

      return [
        isPageLinkField(selectedSourceField) ? selectedSourceField : undefined,
        getDatabaseFields(databaseDraftWithoutDeleted).filter(isPageLinkField),
        cloudId ? selectSupportedProperties(state, cloudId, 'confluence') : undefined
      ] as const;
    }
  );

  useEffect(() => {
    if (cloudId) {
      void dispatch(loadSupportedFields(cloudId, 'confluence'));
    }
  }, [cloudId, dispatch]);

  const handleSourceFieldChange = useCallback(
    (value: DatabaseField | undefined) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_FIELD_ID,
          fieldId: field.id,
          settingsFieldId: value?.id
        })
      );
    },
    [contentId, dispatch, field.id]
  );

  const handlePropertyChange = useCallback(
    (contentField: string) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_CONTENT_FIELD,
          fieldId: field.id,
          contentField
        })
      );
    },
    [contentId, dispatch, field.id]
  );

  return (
    <Stack space="space.050">
      <Field
        label={
          <Text size="small" weight="bold" color="color.text.subtle">
            {formatMessage(messages.pageDetailsSourceFieldLabel)}
          </Text>
        }
        name="page-details-source-field"
        isRequired
      >
        {({ fieldProps }) => (
          <FieldPicker
            fields={sourceFields}
            value={sourceField}
            onChange={handleSourceFieldChange}
            isRequired
            placeholder={formatMessage(
              intlMessages[
                'field-settings.type-page-and-live-doc-link-selector-placeholder'
              ]
            )}
            spacing="compact"
            inputId={fieldProps.id}
            autoSelectFirst
          />
        )}
      </Field>

      <Field
        label={
          <Text size="small" weight="bold" color="color.text.subtle">
            {formatMessage(messages.pageDetailsDisplayedLabel)}
          </Text>
        }
        name="page-details-property"
        isRequired
      >
        {() => (
          <PropertyPicker
            displayedDetail={field.settings.displayedDetail}
            isDisabled={!sourceField || !cloudId}
            isLoading={Boolean(cloudId && !propertyOptions)}
            onChange={handlePropertyChange}
            options={propertyOptions}
            spacing="compact"
            value={field.settings.contentField}
          />
        )}
      </Field>
    </Stack>
  );
};

export default PageDetailsFieldSettings;
